/**
 * Dashboard API client. Session auth with automatic refresh — unlike the
 * Studio, a dashboard user being briefly signed out is merely annoying rather
 * than mid-broadcast catastrophic.
 */

/**
 * Where the API answers.
 *
 * Empty in development, where Vite proxies /v1 to the API on the same origin
 * and relative paths just work. Set at build time for a deployment, because
 * the dashboard is served from Cloudflare Pages on app.stream-cut.com while
 * the API answers on api.stream-cut.com -- a relative /v1 there resolves to
 * Pages, which serves static files and has never heard of /v1. The API's CORS
 * origin is APP_URL, so the cross-origin call this produces is the one it is
 * already configured to accept.
 *
 * Only rooted paths are rewritten. A presigned storage URL is absolute and
 * has to reach the storage provider exactly as issued -- prefixing one would
 * break its signature.
 */
export const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export const apiUrl = (path: string): string =>
  path.startsWith("/") ? API_BASE + path : path;

const ACCESS = "rp_access";
const REFRESH = "rp_refresh";

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

export const tokens = {
  get access() { return localStorage.getItem(ACCESS); },
  get refresh() { return localStorage.getItem(REFRESH); },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS, access);
    localStorage.setItem(REFRESH, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS);
    localStorage.removeItem(REFRESH);
  },
};

async function refreshTokens(): Promise<boolean> {
  const rt = tokens.refresh;
  if (!rt) return false;
  const res = await fetch(apiUrl("/v1/auth/refresh"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ refresh_token: rt }),
  });
  if (!res.ok) {
    tokens.clear();
    return false;
  }
  const t = await res.json();
  tokens.set(t.access_token, t.refresh_token);
  return true;
}

export async function api<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
  retry = true,
): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method,
    headers: {
      ...(tokens.access ? { authorization: `Bearer ${tokens.access}` } : {}),
      ...(body === undefined ? {} : { "content-type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401 && retry && (await refreshTokens())) {
    return api<T>(method, path, body, false);
  }

  if (!res.ok) {
    let code = "internal";
    let message = res.statusText;
    try {
      const j = await res.json();
      code = j.error ?? code;
      message = j.message ?? message;
    } catch { /* non-JSON body */ }
    throw new ApiError(res.status, code, message);
  }

  return res.status === 204 ? (undefined as T) : await res.json();
}

// --- Types shared across views ---------------------------------------------

export interface Recording {
  id: string;
  session_id: string | null;
  /**
   * `local` is a recording that was never uploaded.
   *
   * Its clips were cut on the streamer's own machine and only those were sent,
   * so there is a row, there are reactions, and there is no video here. Every
   * screen that offers to do something with the footage has to know that.
   */
  status: "uploading" | "uploaded" | "analysing" | "ready" | "failed" | "local";
  filename: string | null;
  size_bytes: string | null;
  duration_ms: number | null;
  parts_complete: number;
  parts_total: number | null;
  sync_offset_ms: number | null;
  sync_confidence: number | null;
  drift_ratio: number | null;
  recorded_at: string | null;
  reaction_count: string;
}

export interface Reaction {
  id: string;
  ordinal: number;
  start_ms: number;
  end_ms: number;
  title: string | null;
  edited_by_user: boolean;
  video_title: string | null;
  origin_author: string | null;
  audience_score: number;
  /** The game running when this clip happened. Null for reaction sessions. */
  game_id: string | null;
  game_name: string | null;
  suggested: boolean;
  /**
   * Which signal suggested this clip, for suggested ones only. Null when the
   * streamer marked it themselves, and for suggestions made before the
   * column existed -- all of which were audience peaks.
   */
  suggested_by: "audience" | "voice" | "both" | null;
}

export interface Clip {
  id: string;
  status: "queued" | "rendering" | "ready" | "failed";
  size_bytes: string | null;
  duration_ms: number | null;
  ordinal: number;
  title: string | null;
  video_title: string | null;
  origin_author: string | null;
  recording_id: string;
  failure_reason: string | null;
  audience_score: number;
  suggested: boolean;
  /**
   * Which signal suggested this clip, for suggested ones only. Null when the
   * streamer marked it themselves, and for suggestions made before the
   * column existed -- all of which were audience peaks.
   */
  suggested_by: "audience" | "voice" | "both" | null;
  captions_key: string | null;
  /**
   * The fragment of what was said that matched a search, with the matching
   * words wrapped in SNIPPET_START/SNIPPET_END. Null unless this row came back
   * from a search, and null for clips with no transcript.
   */
  snippet?: string | null;
  /**
   * Where this clip is cut.
   *
   * `local` means the recording never left the streamer's PC and Studio does
   * the cutting, so a queued local clip is waiting for them to open Studio --
   * not for us. Saying "Queued" for both would leave somebody watching a
   * server that is never going to do anything.
   */
  source: "server" | "local";
  /** The game running when this clip happened. Null for reaction sessions. */
  game_id: string | null;
  game_name: string | null;
}
